import { useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';
import FloatingCoachButton from '@/components/FloatingCoachButton';
import PushPrimerSheet from '@/components/PushPrimerSheet';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { subscribeToPush, playNotificationSound, ensurePushPermission } from '@/lib/push';
import useUsageHeartbeat from '@/hooks/useUsageHeartbeat';

const POLL_MS = 25_000;
const SOUND_LAST_SEEN = 'gm_last_notif_id';

export default function AppLayout({ hideNav = false, children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastIdRef = useRef(localStorage.getItem(SOUND_LAST_SEEN) || '');

  // Track time-in-app for usage analytics (admin dashboard). Only runs when
  // a user is logged in; pauses automatically while the tab is hidden.
  useUsageHeartbeat(!!user);

  // Poll notifications. On NEW item: play sound + show in-app toast.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    let timer;

    const tick = async () => {
      try {
        const { data } = await api.get('/notifications');
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        const latest = data[0];
        const lastSeen = lastIdRef.current;
        if (!lastSeen) {
          lastIdRef.current = latest.id;
          localStorage.setItem(SOUND_LAST_SEEN, latest.id);
          return;
        }
        // Find any new items after lastSeen
        const idxNew = data.findIndex((n) => n.id === lastSeen);
        const newOnes = idxNew === -1 ? data.slice(0, 3) : data.slice(0, idxNew);
        if (newOnes.length > 0) {
          playNotificationSound();
          const top = newOnes[0];
          toast(top.title || 'New update', {
            description: top.body || '',
            duration: 4500,
            action: { label: 'Open', onClick: () => navigate('/notifications') },
          });
          lastIdRef.current = latest.id;
          localStorage.setItem(SOUND_LAST_SEEN, latest.id);
        }
      } catch (e) { /* silent */ }
    };

    tick();
    timer = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user, navigate]);

  // Try to subscribe to push if user previously granted permission (silent re-subscribe).
  useEffect(() => {
    if (!user) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') {
      ensurePushPermission().then(() => subscribeToPush()).catch(() => {});
    }
  }, [user]);

  return (
    <div className="gm-page min-h-screen relative bg-[#F4F6FB] sm:border-x sm:border-[#E2E8F0] overflow-x-clip gm-safe-top">
      <div className={hideNav ? '' : 'pb-24'}>
        {children || <Outlet />}
      </div>
      <FloatingCoachButton />
      {!hideNav && <BottomNav />}
      {/* iter-83: rich push-primer sheet replaces the old thin banner. */}
      <PushPrimerSheet />
    </div>
  );
}
